import React, {Component} from 'react';
import PropTypes from 'prop-types';

import * as d3 from 'd3';
import _ from 'lodash';

import {formatKmbt} from '../utils/d3-mixins'

import {geoUtils} from "../utils/geo-utils";

class ScatterPlotGrid extends Component {

  shouldComponentUpdate(nextProps) {
    const widthDifferent = nextProps.width !== this.props.width;
    const heightDifferent = nextProps.height !== this.props.height;
    const dataDifferent = nextProps.data !== this.props.data;
    const dimXDiff = nextProps.dimX !== this.props.dimX;
    const dimYDiff = nextProps.dimY !== this.props.dimY;
    const yearDiff = nextProps.year !== this.props.year;
    const typeDiff = nextProps.type !== this.props.type;

    return widthDifferent || heightDifferent || dataDifferent || dimXDiff || dimYDiff || yearDiff || typeDiff;
  }

  componentDidUpdate() {
    setTimeout(this.renderD3.bind(this), 100);
  }

  linearRegression(data, valueX, valueY) {

    const minX = d3.min(data, d => valueX(d));
    const maxX = d3.max(data, d => valueX(d));
    const maxY = d3.max(data, d => valueY(d));

    let n = data.length;
    let sum_x = 0;
    let sum_y = 0;
    let sum_xy = 0;
    let sum_xx = 0;
    let sum_yy = 0;

    _.forEach(data, d => {
      const x = valueX(d);
      const y = valueY(d);

      sum_x += x;
      sum_y += y;
      sum_xy += (x * y);
      sum_xx += (x * x);
      sum_yy += (y * y);
    });

    const slope = (n * sum_xy - sum_x * sum_y) / (n * sum_xx - sum_x * sum_x);
    const intercept = (sum_y - slope * sum_x) / n;
    const r2 = Math.pow((n * sum_xy - sum_x * sum_y) / Math.sqrt((n * sum_xx - sum_x * sum_x) * (n * sum_yy - sum_y * sum_y)), 2);
    const equation = `y = ${slope.toFixed(5)}x + ${intercept.toFixed(5)}`;
    const ptA = {
      x: minX,
      y: slope * minX + intercept
    };
    const ptB1 = {
      x: maxX,
      y: slope * maxX + intercept
    };
    const ptB2 = {
      y: maxY,
      x: (maxY - intercept) / slope
    };

    // We pick the point that best fits the area so line stays on grid
    const ptB = ptB1.y < maxY ? ptB1 : ptB2;

    return {slope, intercept, r2, equation, ptA, ptB, minX, maxX};
  }

  renderD3() {
    const width = this.props.width - 52;
    const height = this.props.height - 51;
    const props = this.props;

    if (!_.size(props.data)) {
      return;
    }

    const scale = geoUtils.createMultiScale(props.data, props.dimX, props.dimY, props.year, props.type);

    // Filter out zero values since they are redacted locations in most cases with this dataset
    const filteredData = _.filter(props.data, d => {
      return scale.valueX(d) > 0 && scale.valueY(d) > 0;
    });

    const bivarColors = scale.colors;

    const titleX = props.type !== 'y' ? props.titleX : props.titleY;
    const titleY = props.type !== 'z' ? '' : props.titleY;

    const valueId = row => _.get(row, props.dimId);
    const valueX = props.type !== 'y' ? scale.valueX : scale.valueY;
    const valueY = props.type !== 'y' ? scale.valueY : scale.valueX;
    const extentX = props.type !== 'y' ? scale.extentX() : scale.extentY();
    const extentY = props.type !== 'y' ? scale.extentY() : scale.extentX();
    const quantilesX = props.type !== 'y' ? scale.quantilesX() : scale.quantilesY();
    const quantilesY = props.type !== 'y' ? scale.quantilesY() : scale.quantilesX();

    const ticksX = quantilesX;
    ticksX.unshift(extentX[0]);
    ticksX.push(extentX[1]);

    const x = d3.scaleLinear().domain(extentX).range([0, width]);
    const axisX = d3.axisBottom(x).tickValues(ticksX).tickSize(4).tickFormat(formatKmbt);

    const ticksY = props.type === 'z' ? quantilesY : [];
    ticksY.unshift(extentY[0]);
    ticksY.push(extentY[1]);

    const y = d3.scaleLinear().domain(extentY).range([height, 0]);
    const axisY = d3.axisLeft(y).tickValues(ticksY).tickSize(4).tickFormat(formatKmbt);

    // Add the X Axis
    d3.select(this.refs.xAxis)
      .transition()
      .call(axisX);

    // Add the Y Axis
    d3.select(this.refs.yAxis)
      .transition()
      .call(axisY);

    d3.select(this.refs.xTitle)
      .text(titleX || '');

    d3.select(this.refs.yTitle)
      .text(titleY || '');

    // Add colored bg rects
    const rects = d3.select(this.refs.colors)
      .selectAll("rect")
      .data(bivarColors);

    rects.enter().append("rect").each(setRect);
    rects.each(updateRect);
    rects.exit().remove();

    function setRect() {
      d3.select(this)
        .style("fill", function (d) {
          return d.c;
        })
        .attr("width", function (d) {
          return x(ticksX[d.x + 1]) - x(ticksX[d.x]) + 1;
        })
        .attr("height", function (d) {
          return y(ticksY[d.y]) - y(ticksY[d.y + 1]) + 1;
        })
        .attr("y", function (d) {
          return y(ticksY[d.y + 1]);
        })
        .attr("x", function (d) {
          return x(ticksX[d.x]);
        });
    }

    function updateRect() {
      d3.select(this)
        .style("fill", function (d) {
          return d.c;
        })
        .transition()
        .attr("width", function (d) {
          return x(ticksX[d.x + 1]) - x(ticksX[d.x]) + 1;
        })
        .attr("height", function (d) {
          return y(ticksY[d.y]) - y(ticksY[d.y + 1]) + 1;
        })
        .attr("y", function (d) {
          return y(ticksY[d.y + 1]);
        })
        .attr("x", function (d) {
          return x(ticksX[d.x]);
        });
    }

    // Add points
    const points = d3.select(this.refs.points)
      .selectAll("circle")
      .data(filteredData);

    points.enter().append("circle").each(setPoint);
    points.each(updatePoint);
    points.exit().remove();

    function setPoint() {
      d3.select(this)
        .attr("r", 2.5)
        .attr('class', 'hover_out')
        .attr("id", d => "FIPS_" + valueId(d))
        .on("mouseover", d => {
          const coords = [x(valueX(d)), y(valueY(d))];
          if (props.onHoverOver) {
            props.onHoverOver(valueId(d), {coords, props, data: d})
          }
        })
        .on("mouseout", d => {
          if (props.onHoverOut) {
            props.onHoverOut(valueId(d))
          }
        })
        .on("click", d => {
          if (props.onClick) {
            props.onClick(valueId(d))
          }
        })
        .attr("cy", d => y(valueY(d)))
        .attr("cx", d => x(valueX(d)));
    }

    function updatePoint() {
      d3.select(this)
        .attr("r", 2.5)
        .attr('class', 'hover_out')
        .attr("id", d => "FIPS_" + valueId(d))
        .on("mouseover", d => {
          const coords = [x(valueX(d)), y(valueY(d))];
          if (props.onHoverOver) {
            props.onHoverOver(valueId(d), {coords, props, data: d})
          }
        })
        .on("mouseout", d => {
          if (props.onHoverOut) {
            props.onHoverOut(valueId(d))
          }
        })
        .on("click", d => {
          if (props.onClick) {
            props.onClick(valueId(d))
          }
        })
        .transition()
        .attr("cy", d => y(valueY(d)))
        .attr("cx", d => x(valueX(d)));
    }

    const line = d3.select(this.refs.line);

    if (this.props.type === 'z') {
      const reg = this.linearRegression(filteredData, valueX, valueY);

      line
        .on("mouseover", d => {
          const coords = [x(reg.ptB.x), y(reg.ptB.y)];
          if (props.onHoverOver) {

            let level = "None";
            if (Math.abs(reg.r2) > .5) {
              level = "Strong";
            } else if (Math.abs(reg.r2) > .3) {
              level = "Moderate";
            } else if (Math.abs(reg.r2) > .1) {
              level = "Weak";
            }

            const content = (
              <div>
                <b>{reg.equation}</b><br/>
                <b>R-Value : </b><i>{reg.r2.toFixed(4)}</i><br/>
                <b>Correlation : </b><i>{level}</i>
              </div>
            );

            const data = {
              group: 'Trend Line',
              content
            };

            props.onHoverOver(null, {coords, props: {}, data})
          }
        })
        .on("mouseout", d => {
          if (props.onHoverOut) {
            props.onHoverOut(null)
          }
        })
        .transition()
        .attr("x1", x(reg.ptA.x))
        .attr("y1", y(reg.ptA.y))
        .attr("x2", x(reg.ptB.x))
        .attr("y2", y(reg.ptB.y))
    }
    else {
      line.attr("opacity", 0)
    }
  }

  render() {
    return (
      <svg className="ScatterPlotGrid" width={this.props.width} height={this.props.height}>
        <g transform={"translate(30,22)"} ref="svg">
          <g ref="colors"></g>

          <g className="axis">
            <g ref="xAxis" className="xAxis" transform={"translate(0," + (this.props.height - 50) + ")"}></g>
            <g ref="yAxis" className="axis"></g>
            <text ref="xTitle" textAnchor="middle" x={this.props.width / 2 - 22} y={-10}></text>
            <text ref="yTitle" textAnchor="middle"
                  transform={"translate(" + (this.props.width - 42) + "," + (this.props.height / 2 - 22) + ") rotate(90)"}></text>
          </g>

          <g ref="points"></g>

          <g width={this.props.width - 52} height={this.props.height - 52}>
            <line className="regression-line" ref="line"/>
          </g>
        </g>
      </svg>
    );
  }
}

ScatterPlotGrid.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  titleX: PropTypes.string,
  titleY: PropTypes.string,
  dimId: PropTypes.string,
  dimX: PropTypes.string,
  dimY: PropTypes.string,
  data: PropTypes.array,
  onHoverOver: PropTypes.func,
  onHoverOut: PropTypes.func,
  onClick: PropTypes.func
};


export default ScatterPlotGrid



// WEBPACK FOOTER //
// ./src/charts/ScatterPlot/ScatterPlotGrid.js